"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-(--btn-radius) text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--accent) disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-(--btn-primary) text-(--btn-primary-fg) hover:bg-(--btn-primary-hover) font-semibold",
        destructive: "bg-red-600 text-white hover:bg-red-500",
        outline: "border border-(--field-border) bg-transparent text-zinc-100 hover:bg-(--surface-hover) hover:border-(--accent)",
        secondary: "bg-(--surface-strong) text-zinc-100 hover:bg-(--surface-strong-hover)",
        ghost: "hover:bg-(--surface-hover) text-zinc-300 hover:text-zinc-100",
        link: "text-(--accent) underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-(--btn-radius) px-3 text-xs",
        lg: "h-12 rounded-(--btn-radius) px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
